import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Badge } from '../ui/badge';
import {
  Activity,
  Database,
  Users,
  TrendingUp,
  Clock,
  AlertCircle,
  CheckCircle,
  Target,
  Search,
  UserPlus,
  BarChart3,
  Calendar,
  Zap
} from 'lucide-react';
import { doc, getDoc, collection, query, where, getCountFromServer } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { PipelineAnalyticsDocumentClient, PPC_FARM_COLLECTIONS } from '../../types/ppcFarmTypes';

interface PpcFarmAnalyticsDashboardProps {
  className?: string;
}

interface LiveSystemVitals {
  overallStatus: 'healthy' | 'warning' | 'error';
  sellersInQueue: number;
  detailsNotFound: number;
  totalSellers: number;
}

interface SystemThroughput {
  last24h: number;
  last7d: number;
  last30d: number;
}

interface SourceBreakdownRow {
  source: string;
  last24h: number;
  last7d: number;
  last30d: number;
  total: number;
}

const db = getFirestore();

const PpcFarmAnalyticsDashboard: React.FC<PpcFarmAnalyticsDashboardProps> = ({ className = '' }) => {
  const [pipelineData, setPipelineData] = useState<PipelineAnalyticsDocumentClient | null>(null);
  const [sellersInQueue, setSellersInQueue] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPipelineAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch pipeline analytics and sellers in queue in parallel
        const [analyticsSnap, unprocessedSnapshot] = await Promise.all([
          getDoc(doc(db, 'ppcFarm_metadata', 'pipelineAnalytics')),
          getCountFromServer(
            query(collection(db, PPC_FARM_COLLECTIONS.SELLER_IDS), where('status', '==', 'unprocessed'))
          )
        ]);
        
        if (analyticsSnap.exists()) {
          const data = analyticsSnap.data() as PipelineAnalyticsDocumentClient;
          setPipelineData(data);
        } else {
          setError('Pipeline analytics data not found');
        }
        
        // Set real sellers in queue count
        setSellersInQueue(unprocessedSnapshot.data().count);
        
      } catch (error) {
        console.error('Error fetching pipeline analytics:', error);
        setError('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };

    fetchPipelineAnalytics();
  }, []);

  // Calculate time-based metrics from dailyStats
  const calculateTimeBasedMetrics = (
    dailyStats: any,
    field: 'sellersProcessed' | 'newSellersFound',
    days: number
  ): number => {
    if (!dailyStats) return 0;
    
    const now = new Date();
    const cutoffDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));
    
    let total = 0;
    
    // dailyStats is an object where keys are date strings like "2025-09-10"
    Object.entries(dailyStats).forEach(([dateKey, stat]: [string, any]) => {
      if (!stat || typeof stat !== 'object') return;
      
      const statDate = new Date(dateKey);
      if (statDate >= cutoffDate) {
        total += stat[field] || 0;
      }
    });
    
    return total;
  };

  // Calculate source breakdown for time periods
  const calculateSourceBreakdown = (
    dailyStats: any,
    days: number
  ): Record<string, number> => {
    const result = {
      keepa_topseller_daily: 0,
      competitor_extraction: 0,
      raw_scrape_g_sheet: 0,
      keepa_keyword_search: 0,
      target_ledger_migration: 0
    };
    
    if (!dailyStats) return result;
    
    const now = new Date();
    const cutoffDate = new Date(now.getTime() - (days * 24 * 60 * 60 * 1000));
    
    // dailyStats is an object where keys are date strings like "2025-09-10"
    Object.entries(dailyStats).forEach(([dateKey, stat]: [string, any]) => {
      if (!stat || typeof stat !== 'object') return;
      
      const statDate = new Date(dateKey);
      if (statDate >= cutoffDate && stat.sourceBreakdown) {
        (Object.keys(result) as Array<keyof typeof result>).forEach(source => {
          result[source] += stat.sourceBreakdown[source] || 0;
        });
      }
    });
    
    return result;
  };

  // Get live system vitals
  const getLiveSystemVitals = (): LiveSystemVitals => {
    if (!pipelineData) {
      return {
        overallStatus: 'error',
        sellersInQueue: sellersInQueue, // Use real count even when pipelineData is not loaded
        detailsNotFound: 0,
        totalSellers: 0
      };
    }

    // Determine overall status based on pipeline health
    let overallStatus: 'healthy' | 'warning' | 'error' = 'healthy';
    if (pipelineData.pipelineStatus) {
      const lastRunHours = (Date.now() - pipelineData.pipelineStatus.ingestion.lastRunTimestamp.toMillis()) / (1000 * 60 * 60);
      const isIngestionStale = lastRunHours > 26;
      const isIngestionFailed = pipelineData.pipelineStatus.ingestion.lastRunOutcome === 'failure';
      const isProcessingStalled = pipelineData.pipelineStatus.enrichment.processedLastRun === 0;
      
      if (isIngestionFailed || isProcessingStalled) {
        overallStatus = 'error';
      } else if (isIngestionStale) {
        overallStatus = 'warning';
      }
    }

    return {
      overallStatus,
      sellersInQueue: sellersInQueue, // Real count from ppcFarm_sellerIds collection
      detailsNotFound: pipelineData.totals.sellersWithNoData || 0, // Real all-time count from backfill script
      totalSellers: pipelineData.totals.totalSellersInMaster || 0
    };
  };

  // Get system throughput metrics
  const getSystemThroughput = (): SystemThroughput => {
    if (!pipelineData?.dailyStats) {
      return { last24h: 0, last7d: 0, last30d: 0 };
    }

    return {
      last24h: calculateTimeBasedMetrics(pipelineData.dailyStats, 'sellersProcessed', 1),
      last7d: calculateTimeBasedMetrics(pipelineData.dailyStats, 'sellersProcessed', 7),
      last30d: calculateTimeBasedMetrics(pipelineData.dailyStats, 'sellersProcessed', 30)
    };
  };

  // Get source breakdown data
  const getSourceBreakdownData = (): SourceBreakdownRow[] => {
    if (!pipelineData) {
      return [];
    }

    const last24hBreakdown = calculateSourceBreakdown(pipelineData.dailyStats, 1);
    const last7dBreakdown = calculateSourceBreakdown(pipelineData.dailyStats, 7);
    const last30dBreakdown = calculateSourceBreakdown(pipelineData.dailyStats, 30);

    const sourceRows: SourceBreakdownRow[] = [
      {
        source: 'From Keepa Top',
        last24h: last24hBreakdown.keepa_topseller_daily,
        last7d: last7dBreakdown.keepa_topseller_daily,
        last30d: last30dBreakdown.keepa_topseller_daily,
        total: pipelineData.totals.sourceCounts.keepa_topseller_daily || 0
      },
      {
        source: 'From Competitors',
        last24h: last24hBreakdown.competitor_extraction,
        last7d: last7dBreakdown.competitor_extraction,
        last30d: last30dBreakdown.competitor_extraction,
        total: pipelineData.totals.sourceCounts.competitor_extraction || 0
      },
      {
        source: 'From Raw Scrape',
        last24h: last24hBreakdown.raw_scrape_g_sheet,
        last7d: last7dBreakdown.raw_scrape_g_sheet,
        last30d: last30dBreakdown.raw_scrape_g_sheet,
        total: pipelineData.totals.sourceCounts.raw_scrape_g_sheet || 0
      },
      {
        source: 'From Keyword Discovery',
        last24h: last24hBreakdown.keepa_keyword_search,
        last7d: last7dBreakdown.keepa_keyword_search,
        last30d: last30dBreakdown.keepa_keyword_search,
        total: pipelineData.totals.sourceCounts.keepa_keyword_search || 0
      },
      {
        source: 'From Target Ledger',
        last24h: last24hBreakdown.target_ledger_migration,
        last7d: last7dBreakdown.target_ledger_migration,
        last30d: last30dBreakdown.target_ledger_migration,
        total: pipelineData.totals.sourceCounts.target_ledger_migration || 0
      }
    ];

    // Calculate totals row
    const totalsRow: SourceBreakdownRow = {
      source: 'TOTAL FOUND',
      last24h: sourceRows.reduce((sum, row) => sum + row.last24h, 0),
      last7d: sourceRows.reduce((sum, row) => sum + row.last7d, 0),
      last30d: sourceRows.reduce((sum, row) => sum + row.last30d, 0),
      total: sourceRows.reduce((sum, row) => sum + row.total, 0)
    };

    return [...sourceRows, totalsRow];
  };

  // Helper function to get status badge
  const getStatusBadge = (status: 'healthy' | 'warning' | 'error') => {
    switch (status) {
      case 'healthy':
        return (
          <Badge variant="default" className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
            <CheckCircle className="h-3 w-3 mr-1" />
            Healthy
          </Badge>
        );
      case 'warning':
        return (
          <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
            <Clock className="h-3 w-3 mr-1" />
            Warning
          </Badge>
        );
      case 'error':
        return (
          <Badge variant="destructive" className="bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
            <AlertCircle className="h-3 w-3 mr-1" />
            Error
          </Badge>
        );
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  if (error) {
    return (
      <div className={className}>
        <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
          <CardContent className="p-6">
            <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
              <AlertCircle className="h-5 w-5" />
              <span>Error loading analytics: {error}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const liveVitals = getLiveSystemVitals();
  const systemThroughput = getSystemThroughput();
  const sourceBreakdown = getSourceBreakdownData();

  return (
    <div className={className}>
      <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-6">
        Analytics Dashboard
      </h2>

      {/* Tier 1: Live System Vitals */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Live System Vitals
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Overall Status */}
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Overall Status
                  </p>
                </div>
                {loading ? (
                  <Skeleton className="h-6 w-20 mx-auto mb-2" />
                ) : (
                  <div className="flex justify-center mb-2">
                    {getStatusBadge(liveVitals.overallStatus)}
                  </div>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  System health status
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Sellers in Queue */}
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <Database className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Sellers in Queue
                  </p>
                </div>
                {loading ? (
                  <Skeleton className="h-8 w-16 mx-auto mb-2" />
                ) : (
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {liveVitals.sellersInQueue.toLocaleString()}
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Awaiting processing
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Seller Details Not Found */}
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <AlertCircle className="h-4 w-4 text-orange-600 dark:text-orange-400 mr-2" />
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Details Not Found
                  </p>
                </div>
                {loading ? (
                  <Skeleton className="h-8 w-16 mx-auto mb-2" />
                ) : (
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {liveVitals.detailsNotFound.toLocaleString()}
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  All-time count
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Total Sellers */}
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <Users className="h-4 w-4 text-green-600 dark:text-green-400 mr-2" />
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Total Sellers
                  </p>
                </div>
                {loading ? (
                  <Skeleton className="h-8 w-20 mx-auto mb-2" />
                ) : (
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                    {liveVitals.totalSellers.toLocaleString()}
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Enriched
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Tier 2: Sellers Processed */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Sellers Processed
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Last 24 Hours */}
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <Zap className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mr-2" />
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Last 24 Hrs
                  </p>
                </div>
                {loading ? (
                  <Skeleton className="h-8 w-16 mx-auto" />
                ) : (
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {systemThroughput.last24h.toLocaleString()}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Last 7 Days */}
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <BarChart3 className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mr-2" />
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Last 7 Days
                  </p>
                </div>
                {loading ? (
                  <Skeleton className="h-8 w-20 mx-auto" />
                ) : (
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {systemThroughput.last7d.toLocaleString()}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Last 30 Days */}
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="text-center">
                <div className="flex items-center justify-center mb-2">
                  <Calendar className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mr-2" />
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    Last 30 Days
                  </p>
                </div>
                {loading ? (
                  <Skeleton className="h-8 w-24 mx-auto" />
                ) : (
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {systemThroughput.last30d.toLocaleString()}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Tier 3: Sellers Found */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Sellers Found
        </h3>
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Source
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Last 24 Hours
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Last 7 Days
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Last 30 Days
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    // Loading skeleton rows
                    [...Array(6)].map((_, index) => (
                      <tr key={index}>
                        <td className="px-6 py-4">
                          <Skeleton className="h-4 w-32" />
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Skeleton className="h-4 w-16 mx-auto" />
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Skeleton className="h-4 w-16 mx-auto" />
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Skeleton className="h-4 w-16 mx-auto" />
                        </td>
                        <td className="px-6 py-4 text-center">
                          <Skeleton className="h-4 w-20 mx-auto" />
                        </td>
                      </tr>
                    ))
                  ) : (
                    sourceBreakdown.map((row, index) => (
                      <tr 
                        key={row.source}
                        className={row.source === 'TOTAL FOUND' ? 'bg-yellow-50 dark:bg-yellow-900/20 font-bold' : ''}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {row.source}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-center">
                          {row.last24h.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-center">
                          {row.last7d.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-center">
                          {row.last30d.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white text-center">
                          {row.total.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PpcFarmAnalyticsDashboard;